import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MapPin, Zap, Battery, Search, Filter, 
  CheckCircle, Clock, Navigation, Phone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StationsPage = () => {
  const [stations, setStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedStation, setSelectedStation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const response = await axios.get(`${API}/stations`);
        setStations(response.data);
        setFilteredStations(response.data);
      } catch (error) {
        console.error('Error fetching stations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStations();
  }, []);

  useEffect(() => {
    let filtered = stations;
    
    if (selectedCity !== 'all') {
      filtered = filtered.filter(s => s.city.toLowerCase() === selectedCity.toLowerCase());
    }
    
    if (searchQuery) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.city.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredStations(filtered);
  }, [searchQuery, selectedCity, stations]);

  const cities = ['all', ...new Set(stations.map(s => s.city))];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'busy': return 'bg-orange-100 text-orange-700';
      case 'maintenance': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available': return 'I Disponueshëm';
      case 'busy': return 'I Zënë';
      case 'maintenance': return 'Mirëmbajtje';
      default: return status;
    }
  };

  return (
    <div data-testid="stations-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 
              data-testid="stations-title"
              className="text-4xl md:text-5xl font-bold font-['Inter_Tight'] tracking-tight mb-4"
            >
              Stacionet e Karikimit
            </h1>
            <p className="text-lg text-blue-100">
              Gjeni stacionin më të afërt dhe filloni karikimin menjëherë.
            </p>
          </div>
        </div>
      </section>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Filters & List */}
          <div className="lg:col-span-1">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="stations-search"
                  placeholder="Kërko stacion..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-xl border-slate-200"
                />
              </div>
            </div>

            {/* City Filter */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filtro sipas qytetit</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {cities.map((city) => (
                  <button
                    key={city}
                    data-testid={`city-filter-${city}`}
                    onClick={() => setSelectedCity(city)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCity === city
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {city === 'all' ? 'Të Gjitha' : city}
                  </button>
                ))}
              </div>
            </div>

            {/* Stations List */}
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                {filteredStations.length} stacione të gjetura
              </p>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-slate-500">Duke ngarkuar...</p>
                </div>
              ) : (
                filteredStations.map((station) => (
                  <div
                    key={station.id}
                    data-testid={`station-card-${station.id}`}
                    onClick={() => setSelectedStation(station)}
                    className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all ${
                      selectedStation?.id === station.id
                        ? 'border-blue-500 shadow-lg'
                        : 'border-slate-100 hover:border-slate-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 font-['Inter_Tight']">
                          {station.name}
                        </h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {station.address}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(station.status)}`}>
                        {getStatusText(station.status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-blue-700">
                        <Zap className="w-4 h-4" />
                        {station.max_power} kW
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Battery className="w-4 h-4" />
                        {station.connectors} pika
                      </span>
                      <span className="text-slate-400">
                        {station.charger_types.join(' / ')}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Map & Details */}
          <div className="lg:col-span-2">
            {/* Map Placeholder */}
            <div 
              data-testid="map-container"
              className="map-container h-96 rounded-3xl mb-6 flex items-center justify-center relative"
            >
              <div className="text-center z-10 glass bg-white/95 backdrop-blur-xl rounded-2xl p-8">
                <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Harta e Stacioneve
                </h3>
                <p className="text-slate-500 text-sm">
                  Shkarkoni aplikacionin për hartën interaktive
                </p>
                <div className="flex justify-center gap-3 mt-4">
                  <a 
                    href="https://apps.apple.com/hr/app/albmove/id6759310357" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    App Store
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.albmove.client" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Google Play
                  </a>
                </div>
              </div>
              
              {/* Decorative pins */}
              <div className="absolute top-20 left-20 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="absolute top-32 right-32 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="absolute bottom-24 left-40 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="absolute bottom-32 right-24 w-8 h-8 bg-lime-400 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Zap className="w-4 h-4 text-slate-900" />
              </div>
            </div>

            {/* Selected Station Details */}
            {selectedStation ? (
              <div 
                data-testid="station-details"
                className="bg-white rounded-3xl p-8 border border-slate-200/50 shadow-lg"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 font-['Inter_Tight']">
                      {selectedStation.name}
                    </h2>
                    <p className="text-slate-500 flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" />
                      {selectedStation.address}, {selectedStation.city}
                    </p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(selectedStation.status)}`}>
                    {getStatusText(selectedStation.status)}
                  </span>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-slate-50 rounded-2xl p-5">
                    <Zap className="w-8 h-8 text-blue-600 mb-3" />
                    <p className="text-2xl font-bold text-slate-900">{selectedStation.max_power} kW</p>
                    <p className="text-sm text-slate-500">Fuqi Maksimale</p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-5">
                    <Battery className="w-8 h-8 text-blue-600 mb-3" />
                    <p className="text-2xl font-bold text-slate-900">{selectedStation.connectors}</p>
                    <p className="text-sm text-slate-500">Pika Karikimi</p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-5">
                    <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                    <p className="text-2xl font-bold text-slate-900">{selectedStation.charger_types.join(' & ')}</p>
                    <p className="text-sm text-slate-500">Lloji i Karikimit</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button 
                    data-testid="navigate-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedStation.latitude},${selectedStation.longitude}`, '_blank')}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigo
                  </Button>
                  <Button 
                    data-testid="call-support-button"
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Mbështetje
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 rounded-3xl p-12 text-center">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Zgjidhni një Stacion
                </h3>
                <p className="text-slate-500">
                  Klikoni mbi një stacion nga lista për të parë detajet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationsPage;
